import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { Box, IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import type { ProductSize } from "../models/ProductSize";
import { activeColumnDef } from "@/controls/ag-grid/components/ActiveColumn";

interface UseTableDataProps {
	onDelete: (product: ProductSize) => void;
	onToggleActive: (product: ProductSize, active: boolean) => void;
}

export const useTableData = ({
	onDelete,
	onToggleActive,
}: UseTableDataProps) => {
	const columnDefs: ColDef<ProductSize>[] = [
		{
			field: "Name",
			headerName: "Name",
			flex: 1.5,
			sortable: true,
			filter: true,
		},
		{
			field: "ProductTypeName",
			headerName: "Product Type",
			flex: 1.2,
			sortable: true,
			filter: true,
		},
		{
			field: "SectionCode",
			headerName: "Section Code",
			flex: 0.8,
			sortable: true,
		},
		{
			field: "FinishSpeedGoal",
			headerName: "Fin Speed",
			flex: 0.7,
			valueFormatter: (params: { value: number | null }) =>
				params.value != null ? params.value.toFixed(2) : "",
		},
		{
			field: "Stand1GapGoal",
			headerName: "Gap St 1",
			flex: 0.7,
			valueFormatter: (params: { value: number | null }) =>
				params.value != null ? params.value.toFixed(2) : "",
		},
		{
			field: "Stand17GapGoal",
			headerName: "Gap St 17",
			flex: 0.7,
			valueFormatter: (params: { value: number | null }) =>
				params.value != null ? params.value.toFixed(2) : "",
		},
		{
			field: "TPHGoal",
			headerName: "TPH Goal",
			flex: 0.7,
			valueFormatter: (params: { value: number | null }) =>
				params.value != null ? params.value.toFixed(2) : "",
		},
		{
			field: "Weight",
			headerName: "Weight/Ft",
			flex: 0.7,
			valueFormatter: (params: { value: number | null }) =>
				params.value != null ? params.value.toFixed(2) : "",
		},
		{
			field: "RevisionTimestamp",
			headerName: "Revision Date",
			flex: 0.9,
			valueGetter: (params) =>
				dxtToLocalServerTime(
					`${params.data?.RevisionTimestamp}`,
					"MM/dd/yyyy",
				) || "",
		},
		{
			field: "RevisionUserName",
			headerName: "Revision User",
			flex: 0.8,
			sortable: true,
		},
		activeColumnDef<ProductSize>({ onToggle: onToggleActive }),
		{
			headerName: "",
			width: 60,
			minWidth: 60,
			maxWidth: 60,
			sortable: false,
			filter: false,
			resizable: false,
			suppressSizeToFit: true,
			cellRenderer: (params: ICellRendererParams<ProductSize>) => {
				if (!params.data) return null;
				return (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<Tooltip title="Delete">
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									onDelete(params.data as ProductSize);
								}}
								color="error"
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				);
			},
		},
	];

	return { columnDefs };
};
